/**
 * Tomo domain model — plain, immutable snapshots of what lives in a project's Y.Doc.
 * The Y.Doc layout itself is documented in `project-doc.ts`.
 */

/** Categorical palette shared by tags, select options, sticky notes and project colors. */
export const COLOR_NAMES = ['gray', 'brown', 'orange', 'yellow', 'green', 'teal', 'blue', 'purple', 'pink', 'red'] as const;
export type ColorName = (typeof COLOR_NAMES)[number];

/** Game-flavoured aliases for the palette (used by rarity fields in templates). */
export const RARITY_COLORS = {
  common: 'gray',
  uncommon: 'green',
  rare: 'blue',
  epic: 'purple',
  legendary: 'orange',
  mythic: 'red',
} as const satisfies Record<string, ColorName>;

export const isColorName = (v: unknown): v is ColorName => typeof v === 'string' && (COLOR_NAMES as readonly string[]).includes(v);

/* ----------------------------------------------------------------------------------------------
 * Project
 * -------------------------------------------------------------------------------------------- */

/**
 * The parts of a project that aren't collections and that there is only ever one of. They are
 * added and removed like a collection, except that removing one only hides it: its boards or
 * polls stay in the document and come back if it is added again.
 */
export type ProjectSection = 'boards' | 'votes';

export const PROJECT_SECTIONS: readonly ProjectSection[] = ['boards', 'votes'];

export const isProjectSection = (v: unknown): v is ProjectSection => (PROJECT_SECTIONS as readonly string[]).includes(v as string);

export interface ProjectMeta {
  id: string;
  name: string;
  description: string;
  /** Icon token: `i:<icon-name>`. */
  icon: string;
  color: ColorName;
  createdAt: number;
  schemaVersion: number;
  template: string;
  /** Which sections the project has. Documents written before sections have all of them. */
  sections: readonly ProjectSection[];
}

/* ----------------------------------------------------------------------------------------------
 * Collections, fields and views
 * -------------------------------------------------------------------------------------------- */

export type FieldType =
  | 'text'
  | 'number'
  | 'select'
  | 'multiSelect'
  | 'checkbox'
  | 'date'
  | 'person'
  | 'relation'
  | 'url'
  | 'rating'
  | 'range'
  | 'dice'
  | 'color'
  | 'formula'
  | 'votes'
  | 'file';

/** Special meaning a field can carry, so a card knows which of them to put where. */
export type FieldRole = 'status' | 'assignee' | 'priority' | 'due' | 'votes' | 'tags' | 'owner';

export interface SelectOption {
  id: string;
  label: string;
  color: ColorName;
}

export interface FieldConfig {
  /* number / range / rating */
  decimals?: number;
  min?: number;
  max?: number;
  prefix?: string;
  suffix?: string;
  /** Render numbers as a stat bar (needs `max`). */
  display?: 'number' | 'bar';
  /* select / multiSelect */
  options?: SelectOption[];
  /* relation / person */
  targetCollectionId?: string | null;
  multiple?: boolean;
  /* date */
  includeTime?: boolean;
  /* formula: field references are stored as `{fieldId}` */
  expression?: string;
  /* votes */
  allowDownvotes?: boolean;
}

export interface FieldDef {
  id: string;
  name: string;
  type: FieldType;
  order: string;
  config: FieldConfig;
  description?: string;
  role?: FieldRole;
}

export type ViewType = 'table' | 'board' | 'gallery' | 'list';

/** Sort/filter targets: a field id or one of these record properties. */
export const SYSTEM_KEYS = {
  title: '_title',
  created: '_created',
  updated: '_updated',
} as const;

export interface SortRule {
  fieldId: string;
  dir: 'asc' | 'desc';
}

export type FilterOp =
  | 'contains'
  | 'notContains'
  | 'is'
  | 'isNot'
  | 'isEmpty'
  | 'isNotEmpty'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'hasAny'
  | 'hasAll'
  | 'hasNone'
  | 'checked'
  | 'unchecked'
  | 'before'
  | 'after';

export interface FilterRule {
  id: string;
  fieldId: string;
  op: FilterOp;
  value?: unknown;
}

export interface ViewDef {
  id: string;
  name: string;
  type: ViewType;
  order: string;
  sort: SortRule[];
  filters: FilterRule[];
  filterMode: 'and' | 'or';
  /** Board column field (select, person or checkbox). */
  groupBy: string | null;
  hiddenFields: string[];
  fieldWidths: Record<string, number>;
  /** Where a card's picture comes from: a file or relation field, or the record's own cover. */
  coverFieldId: string | null;
  cardSize: 'sm' | 'md' | 'lg';
}

export interface CollectionData {
  id: string;
  name: string;
  icon: string;
  color: ColorName;
  description: string;
  /** Label of the primary (title) column, e.g. "Name", "Title", "Task". */
  titleLabel: string;
  /** The group holding it in the sidebar, or `null` for a collection that sits on its own. */
  groupId: string | null;
  order: string;
  createdAt: number;
  deletedAt: number | null;
  fields: FieldDef[];
  views: ViewDef[];
}

/**
 * A group in the sidebar's collection list, the equivalent of a folder. It holds nothing of its
 * own — only a name and a place — so deleting one leaves its collections in the project, right
 * where the group used to be.
 */
export interface CollectionGroup {
  id: string;
  name: string;
  order: string;
  createdAt: number;
}

/**
 * One top-level row of the collection list: a collection on its own, or a group with the
 * collections inside it. Groups and loose collections share a single order, so a group can sit
 * anywhere in the list. Groups never nest.
 */
export type CollectionTreeNode =
  | { kind: 'collection'; id: string; order: string; collection: CollectionData }
  | { kind: 'group'; id: string; order: string; group: CollectionGroup; collections: CollectionData[] };

/* ----------------------------------------------------------------------------------------------
 * Records
 * -------------------------------------------------------------------------------------------- */

export interface FileMeta {
  /** Lowercase hex SHA-256 of the bytes (content address in the blob store). */
  hash: string;
  name: string;
  mime: string;
  size: number;
  width?: number;
  height?: number;
  /** Seconds, for audio/video. */
  duration?: number;
  /** Blob hash of a small preview image (images, videos). */
  thumb?: string;
  /** Normalised 0..1 waveform peaks for audio files. */
  peaks?: number[];
}

export interface RecordData {
  id: string;
  collectionId: string;
  title: string;
  icon: string | null;
  order: string;
  parentId: string | null;
  values: Readonly<Record<string, unknown>>;
  /**
   * The one picture a record carries without any field for it: shown above the record and on its
   * card. Anything else it holds — more images, audio, reference — goes in a `file` field.
   */
  cover: FileMeta | null;
  /** userId → +1 / -1 (only collections with a votes field use this). */
  votes: Readonly<Record<string, number>>;
  createdAt: number;
  createdBy: string | null;
  updatedAt: number;
  updatedBy: string | null;
  deletedAt: number | null;
}

/* ----------------------------------------------------------------------------------------------
 * Polls ("Votes")
 * -------------------------------------------------------------------------------------------- */

export type PollMode = 'single' | 'multiple' | 'score';

export interface PollOption {
  id: string;
  label: string;
  description?: string;
  /** Optional record this option stands for (e.g. a unit or an asset). */
  recordId?: string | null;
}

export interface PollVote {
  /** Option ids (single / multiple). */
  choices?: string[];
  /** optionId → 1..5 (score mode). */
  scores?: Record<string, number>;
  at: number;
}

export interface PollData {
  id: string;
  question: string;
  description: string;
  mode: PollMode;
  options: PollOption[];
  anonymous: boolean;
  status: 'open' | 'closed';
  /** Epoch ms deadline, informative (polls are closed explicitly or when the deadline passes). */
  closesAt: number | null;
  /** Winning option chosen when closing. */
  decision: string | null;
  decisionNote: string;
  linkedRecordId: string | null;
  order: string;
  createdAt: number;
  createdBy: string | null;
  closedAt: number | null;
  deletedAt: number | null;
  votes: Readonly<Record<string, PollVote>>;
}

/* ----------------------------------------------------------------------------------------------
 * Boards
 * -------------------------------------------------------------------------------------------- */

export type BoardItemType = 'note' | 'text' | 'shape' | 'draw' | 'image';

/** The geometry a `shape` item draws inside its box. */
export const BOARD_SHAPES = ['rect', 'ellipse', 'diamond', 'line', 'arrow'] as const;
export type BoardShape = (typeof BOARD_SHAPES)[number];

/**
 * What people can vote on: the items that carry an idea or a reference, not the decorations. A
 * label, a shape and a stroke annotate the board rather than propose anything.
 */
export const isVotable = (type: BoardItemType) => type === 'note' || type === 'image';

export interface BoardData {
  id: string;
  name: string;
  description: string;
  order: string;
  createdAt: number;
  createdBy: string | null;
  deletedAt: number | null;
}

export interface BoardItem {
  id: string;
  type: BoardItemType;
  x: number;
  y: number;
  w: number;
  h: number;
  color: ColorName;
  text: string;
  z: number;
  createdAt: number;
  createdBy: string | null;
  /**
   * The people who voted for this item, as `userId → 1`. One vote each, no budget, so the count of
   * keys is the score. (Boards once had a dot budget; a stray larger number reads back as one.)
   */
  votes: Readonly<Record<string, number>>;
  /** Set once the note was promoted to an idea record. */
  promotedTo: string | null;
  /** Which geometry a `shape` item draws. Ignored by the other types. */
  shape: BoardShape;
  /**
   * Freehand ink for a `draw` item: a flat `[x0, y0, x1, y1, …]` list of points in the item's own
   * space, each 0..1 of its width and height. Normalised so moving or resizing a stroke is one
   * write instead of a rewrite of every point.
   */
  points: readonly number[];
  /** Stroke width in world units, for `shape` and `draw`. */
  strokeWidth: number;
  /** Shapes only: paint the interior in the item's color rather than just its outline. */
  filled: boolean;
  /** The picture an `image` item shows. */
  file: FileMeta | null;
}

/* ----------------------------------------------------------------------------------------------
 * People (denormalised profiles so offline copies can render names/avatars)
 * -------------------------------------------------------------------------------------------- */

export interface Person {
  id: string;
  name: string;
  color: string;
  /** Sample teammates from templates are flagged so the UI can label them. */
  sample?: boolean;
}
